import { Button, Checkbox, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import type { Consent, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CONSENT_DEFINITIONS,
  CONTACT_CHANNELS,
  type ConsentDefinition,
  FEATURED_CONSENT_KEY,
  findConsentForDefinition,
  getConsentChannels,
  isConsentActive,
  SMS_CONSENT_KEY,
  withChannelsOnConsent,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import { IconDownload } from '@tabler/icons-react';
import { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { InformedConsentModal } from '../../components/InformedConsentModal';
import { withTenantScope } from '../../utils/tenant';
import { ConsentCard } from './ConsentCard';
import classes from './ConsentCard.module.css';
import { RevokeConsentModal } from './RevokeConsentModal';
import { friendlyErrorMessage } from './settings.utils';

interface ConsentsSectionProps {
  patient: Patient;
  consents: Consent[];
  onConsentsUpdated: (consents: Consent[]) => void;
}

const OTHERS_ORDER = ['informed-consent', 'release-of-information', 'consent-to-market'];
const INFORMED_CONSENT_SIGNATURE_URL = 'http://polarissleep.com/fhir/StructureDefinition/informed-consent-signature';

function downloadPdfBase64(data: string, patient: Patient): void {
  const bytes = Uint8Array.from(atob(data), (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `informed-consent-${(patient.name?.[0]?.family ?? 'patient').toLowerCase()}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function downloadInformedConsent(consent: Consent, patient: Patient): void {
  const encoded = consent.sourceAttachment?.data;
  if (!encoded) {
    showNotification({ color: 'red', message: 'Full signed agreement is not available for this consent.' });
    return;
  }
  const bytes = Uint8Array.from(atob(encoded), (character) => character.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `informed-consent-${(patient.name?.[0]?.family ?? 'patient').toLowerCase()}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
function setsEqual(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const x of a) {
    if (!b.has(x)) {
      return false;
    }
  }
  return true;
}

interface ContactChannelsFormProps {
  channels: Set<string>;
  smsActive: boolean;
  optedOut: boolean;
  readOnly: boolean;
  loading: boolean;
  onChannelToggle: (code: string, checked: boolean) => void;
  onSmsToggle: (checked: boolean) => void;
  onOptOutToggle: (checked: boolean) => void;
}

function ContactChannelsForm({
  channels,
  smsActive,
  optedOut,
  readOnly,
  loading,
  onChannelToggle,
  onSmsToggle,
  onOptOutToggle,
}: ContactChannelsFormProps): JSX.Element {
  const disabled = loading || readOnly;
  return (
    <Group className={classes.formSection} justify="space-between" align="center" wrap="wrap" gap="md">
      <Text fz={16} fw={600} c="black">
        Best method to reach you:
      </Text>
      <Group gap="lg" wrap="wrap">
        <Checkbox
          label="Text Message"
          color="polaris.5"
          checked={smsActive}
          disabled={disabled || optedOut}
          onChange={(e) => onSmsToggle(e.currentTarget.checked)}
        />
        {CONTACT_CHANNELS.map((ch) => (
          <Checkbox
            key={ch.code}
            label={ch.label}
            color="polaris.5"
            checked={channels.has(ch.code)}
            disabled={disabled || optedOut}
            onChange={(e) => onChannelToggle(ch.code, e.currentTarget.checked)}
          />
        ))}
        <Checkbox
          label="Opt Out of All Contact"
          color="polaris.5"
          checked={optedOut}
          disabled={disabled}
          onChange={(e) => onOptOutToggle(e.currentTarget.checked)}
        />
      </Group>
    </Group>
  );
}

export function ConsentsSection({ patient, consents, onConsentsUpdated }: ConsentsSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [pendingChannels, setPendingChannels] = useState<Set<string> | null>(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [informedConsentOpen, setInformedConsentOpen] = useState(false);

  const { featured, others } = useMemo(() => {
    const feat = CONSENT_DEFINITIONS.find((d) => d.key === FEATURED_CONSENT_KEY) ?? null;
    const byKey = new Map(CONSENT_DEFINITIONS.map((d) => [d.key, d]));
    const rest = OTHERS_ORDER.map((k) => byKey.get(k)).filter((d): d is ConsentDefinition => Boolean(d));
    return { featured: feat, others: rest };
  }, []);

  const consentsRef = useRef(consents);
  consentsRef.current = consents;

  const effectiveActive = useCallback(
    (def: ConsentDefinition): boolean => {
      if (def.key in pending) {
        return pending[def.key];
      }
      return isConsentActive(findConsentForDefinition(consents, def));
    },
    [consents, pending]
  );

  const featuredConsent = featured ? findConsentForDefinition(consents, featured) : undefined;
  const informedConsentDefinition = CONSENT_DEFINITIONS.find((definition) => definition.key === 'informed-consent');
  const informedConsent = informedConsentDefinition
    ? findConsentForDefinition(consents, informedConsentDefinition)
    : undefined;
  const effectiveChannels = pendingChannels ?? getConsentChannels(featuredConsent);
  const smsDef = useMemo(() => CONSENT_DEFINITIONS.find((d) => d.key === SMS_CONSENT_KEY), []);
  const smsActive = smsDef ? effectiveActive(smsDef) : false;

  const handleCardToggle = useCallback(
    (def: ConsentDefinition, newActive: boolean) => {
      setPending((prev) => ({ ...prev, [def.key]: newActive }));
      if (def.key === FEATURED_CONSENT_KEY && !newActive) {
        setPendingChannels(new Set());
        if (smsDef) {
          setPending((prev) => ({ ...prev, [smsDef.key]: false }));
        }
      }
    },
    [smsDef]
  );

  const handleChannelToggle = useCallback(
    (code: string, checked: boolean) => {
      if (!featured) {
        return;
      }
      const base =
        pendingChannels ?? new Set(getConsentChannels(findConsentForDefinition(consentsRef.current, featured)));
      const next = new Set(base);
      if (checked) {
        next.add(code);
        if (!effectiveActive(featured)) {
          setPending((prev) => ({ ...prev, [featured.key]: true }));
        }
      } else {
        next.delete(code);
      }
      setPendingChannels(next);
    },
    [featured, pendingChannels, effectiveActive]
  );

  const handleSmsToggle = useCallback(
    (checked: boolean) => {
      if (!smsDef || !featured) {
        return;
      }
      setPending((prev) => {
        const next = { ...prev, [smsDef.key]: checked };
        if (checked && !(featured.key in next ? next[featured.key] : isConsentActive(featuredConsent))) {
          next[featured.key] = true;
        }
        return next;
      });
    },
    [smsDef, featured, featuredConsent]
  );

  const handleOptOutToggle = useCallback(
    (checked: boolean) => {
      if (!featured) {
        return;
      }
      setPending((prev) => {
        const next = { ...prev, [featured.key]: !checked };
        if (checked && smsDef) {
          next[smsDef.key] = false;
        }
        return next;
      });
      if (checked) {
        setPendingChannels(new Set());
      }
    },
    [featured, smsDef]
  );

  const diffs = useMemo(() => {
    const grants: ConsentDefinition[] = [];
    const revokes: ConsentDefinition[] = [];
    for (const def of CONSENT_DEFINITIONS) {
      if (!(def.key in pending)) {
        continue;
      }
      const desired = pending[def.key];
      const current = isConsentActive(findConsentForDefinition(consents, def));
      if (desired === current) {
        continue;
      }
      if (desired) {
        grants.push(def);
      } else {
        revokes.push(def);
      }
    }
    return { grants, revokes };
  }, [pending, consents]);

  const channelsDiffer = useMemo(() => {
    if (!pendingChannels || !featured) {
      return false;
    }
    return !setsEqual(pendingChannels, getConsentChannels(featuredConsent));
  }, [pendingChannels, featured, featuredConsent]);

  const persistChange = useCallback(
    async (
      definition: ConsentDefinition,
      newActive: boolean,
      channels: Set<string> | null,
      working: Consent[]
    ): Promise<Consent[]> => {
      const existing = findConsentForDefinition(working, definition);
      const saved = await Sentry.startSpan(
        { op: 'http.client', name: `Toggle Consent: ${definition.title}` },
        async () => {
          const base: Consent = existing?.id
            ? { ...existing, status: newActive ? 'active' : 'rejected' }
            : withTenantScope({
                resourceType: 'Consent',
                patient: createReference(patient),
                status: newActive ? 'active' : 'rejected',
                scope: definition.scope,
                category: [definition.category],
                policyRule: definition.policyRule,
                dateTime: new Date().toISOString(),
              });
          const next = channels !== null ? withChannelsOnConsent(base, channels) : base;
          return existing?.id ? medplum.updateResource(next) : medplum.createResource(next);
        }
      );
      return existing?.id ? working.map((c) => (c.id === existing.id ? saved : c)) : [...working, saved];
    },
    [medplum, patient]
  );

  const commit = useCallback(async () => {
    setSaving(true);
    let working = consentsRef.current;
    try {
      for (const def of CONSENT_DEFINITIONS) {
        const isFeatured = def.key === FEATURED_CONSENT_KEY;
        const toggleChange = def.key in pending;
        const channelChange = isFeatured && pendingChannels !== null;
        if (!toggleChange && !channelChange) {
          continue;
        }
        const existing = findConsentForDefinition(working, def);
        const currentActive = isConsentActive(existing);
        const desiredActive = toggleChange ? pending[def.key] : currentActive;
        const currentChannels = getConsentChannels(existing);
        const desiredChannels = channelChange ? pendingChannels : null;
        const channelsChangedHere = channelChange && !setsEqual(desiredChannels as Set<string>, currentChannels);
        if (desiredActive === currentActive && !channelsChangedHere) {
          continue;
        }
        working = await persistChange(def, desiredActive, desiredChannels, working);
        onConsentsUpdated(working);
      }
      setPending({});
      setPendingChannels(null);
      setEditMode(false);
    } catch (error) {
      Sentry.captureException(error);
      onConsentsUpdated(consentsRef.current);
      showNotification({ color: 'red', message: friendlyErrorMessage(error) });
    } finally {
      setSaving(false);
      setRevokeOpen(false);
    }
  }, [pending, pendingChannels, persistChange, onConsentsUpdated]);

  const handleHeaderButton = useCallback(() => {
    if (!editMode) {
      setPending({});
      setPendingChannels(null);
      setEditMode(true);
      return;
    }
    if (diffs.grants.length === 0 && diffs.revokes.length === 0 && !channelsDiffer) {
      setEditMode(false);
      return;
    }
    if (diffs.revokes.length > 0) {
      setRevokeOpen(true);
      return;
    }
    commit().catch(() => undefined);
  }, [editMode, diffs, channelsDiffer, commit]);

  const handleRevokeCancel = useCallback(() => {
    setRevokeOpen(false);
  }, []);

  return (
    <>
      <InformedConsentModal
        opened={informedConsentOpen}
        onClose={() => setInformedConsentOpen(false)}
        dateOfBirth={patient.birthDate}
        onConfirm={() => undefined}
        submitting={false}
        viewOnly
        signedSignature={
          informedConsent?.extension?.find((extension) => extension.url === INFORMED_CONSENT_SIGNATURE_URL)?.valueString
        }
        signedRelationship={
          informedConsent?.extension?.find(
            (extension) =>
              extension.url === 'http://polarissleep.com/fhir/StructureDefinition/informed-consent-relationship'
          )?.valueString as 'Self' | 'Legal guardian' | undefined
        }
        onDownload={async (agreementPdfBase64) => {
          if (!informedConsent?.id) {
            return;
          }
          const saved = await medplum.updateResource({
            ...informedConsent,
            sourceAttachment: {
              contentType: 'application/pdf',
              title: 'Informed Consent for Telehealth',
              data: agreementPdfBase64,
            },
          });
          onConsentsUpdated(consents.map((consent) => (consent.id === saved.id ? saved : consent)));
          downloadPdfBase64(agreementPdfBase64, patient);
        }}
      />
      <Paper
        withBorder
        p={20}
        radius="md"
        shadow="xs"
        style={{
          borderColor: editMode ? 'var(--mantine-primary-color-filled)' : undefined,
          borderWidth: editMode ? 2 : undefined,
          transition: 'border-color 0.2s, border-width 0.2s',
        }}
      >
        <Stack gap={10}>
          <Group justify="space-between" align="center">
            <Title order={2} fz={24} lh="32px" fw={700}>
              Consents
            </Title>
            <Button color="polaris.5" size="xs" loading={saving} onClick={handleHeaderButton} aria-pressed={editMode}>
              {editMode ? 'Save' : 'Edit'}
            </Button>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={16}>
            {others.map((def) => (
              <ConsentCard
                key={def.key}
                title={def.title}
                description={def.description}
                active={effectiveActive(def)}
                loading={saving}
                required={def.required}
                readOnly={!editMode}
                onToggle={(active) => handleCardToggle(def, active)}
                agreementAction={
                  def.key === 'informed-consent' ? (
                    <Group gap="sm">
                      <Text
                        component="button"
                        type="button"
                        fz={11}
                        fw={600}
                        c="polaris.5"
                        style={{ lineHeight: '14px', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                        onClick={() => setInformedConsentOpen(true)}
                      >
                        View Full Agreement
                      </Text>
                      {effectiveActive(def) && (
                        <Button
                          variant="subtle"
                          size="compact-xs"
                          px={0}
                          leftSection={<IconDownload size={14} />}
                          onClick={() => {
                            const consent = findConsentForDefinition(consentsRef.current, def);
                            if (consent) {
                              if (consent.sourceAttachment?.data) {
                                downloadInformedConsent(consent, patient);
                              } else {
                                setInformedConsentOpen(true);
                              }
                            }
                          }}
                        >
                          Download
                        </Button>
                      )}
                    </Group>
                  ) : undefined
                }
              />
            ))}
          </SimpleGrid>
          {featured && (
            <ConsentCard
              key={featured.key}
              title={featured.title}
              description={featured.description}
              active={effectiveActive(featured)}
              loading={saving}
              required={featured.required}
              readOnly={!editMode}
              featured
              onToggle={(active) => handleCardToggle(featured, active)}
              channelsForm={
                <ContactChannelsForm
                  channels={effectiveChannels}
                  smsActive={smsActive}
                  optedOut={!effectiveActive(featured)}
                  readOnly={!editMode}
                  loading={saving}
                  onChannelToggle={handleChannelToggle}
                  onSmsToggle={handleSmsToggle}
                  onOptOutToggle={handleOptOutToggle}
                />
              }
            />
          )}
        </Stack>
      </Paper>
      <RevokeConsentModal
        opened={revokeOpen}
        definitions={diffs.revokes}
        onCancel={handleRevokeCancel}
        onConfirm={() => {
          commit().catch(() => undefined);
        }}
      />
    </>
  );
}
